/**
 * PLT-5003: in-chat "you've reached your usage limit" card.
 *
 * Rendered when the chat backend returns 429 with
 * `errorCode: AI_CHAT_TOKEN_LIMIT_REACHED` (PLT-4503 enforcement). The
 * adapter turns that response into a synthetic `ai_chat_token_limit`
 * tool-call (see `makeTokenLimitContent` in upright-chat-shared.ts), which
 * this card renders instead of the generic red error block.
 *
 * Copy rules (from PLT-4988):
 *  - Generic headline — NO number is shown to anyone (honours "don't
 *    communicate Starter's quota").
 *  - The CTA varies by tier; see `useTokenLimitCtaVariant`. Exact wording is
 *    pending a marketing/product blessing (parent open call #3) and is
 *    deliberately kept in one place here so it's trivial to tweak.
 *
 * PLT-5266: when the blocked turn was metered against a DMA Consultant client
 * company that is still on Starter, and the caller is an admin who can pay for
 * it, the card offers a self-serve Starter→Pro upgrade for that company — the
 * same flow as the consultant dashboard "Upgrade plan" button. Everyone else
 * keeps the tier-based contact CTA.
 */
import { InfoCircle } from '@upright/ui/icons';
import { useGetLoggedInUser } from '@upright/ui/common/generic/hooks';
import { getOrganizationOfUser } from '@upright/common/iam/organizations/common';
import { isDmaConsultantUser } from '@upright/common/utils';
import { useNavigateWithSearch } from '@upright/ui/common/generic/components/navigation/link-with-search';
import { useOrganizationAccess } from '@upright/ui/views/dma-v2-view/dashboard/use-organization-access';
import { useGetCompanyUserGroups } from '@upright/ui/api/model/input/companies/company-user-groups';
import { useGetCompanySubscription } from '@upright/ui/views/dma-v2-view/shared/hooks/use-get-company-subscription';
import { useUpgradeFlow } from '@upright/ui/views/dma-v2-view/shared/upgrade-modal/use-upgrade-flow';
import { TierSelectionModal } from '@upright/ui/views/dma-v2-view/shared/upgrade-modal/tier-selection-modal';
import { UpgradeStripeCheckoutModal } from '@upright/ui/views/dma-v2-view/shared/upgrade-modal/upgrade-stripe-checkout-modal';
import {
  UpgradeSuccessModal,
  UpgradeConfirmingPaymentModal,
} from '@upright/ui/views/dma-v2-view/shared/upgrade-modal/upgrade-success-modal';
import { UpgradeErrorModal } from '@upright/ui/views/dma-v2-view/shared/upgrade-modal/upgrade-error-modal';
import { useMaybeCsrdCompanyContext } from '@upright/ui/common/upright/model/output/scores/csrd/csrd-company-double-materiality/context';
import { useTokenLimitCtaVariant } from './use-token-limit-cta-variant';

// Placeholder support address — same one the previous plain-text limit
// message used. Swap for the blessed contact route when copy is finalised.
const SUPPORT_EMAIL = 'support@uprightplatform.com';

// Org-level Plans & billing page for DMA Freemium orgs. DMAV2PageView resolves
// the company from the org itself, so no company id/name is needed in the path.
const PLANS_PATH = '/dashboard/settings/plans';

/**
 * PLT-5273: the per-tool usage quotas (compute_financial_effects and the
 * climate-risk / site tools) report a limit hit as a tool result whose `error`
 * is one of these two kinds (see chat/index.ts). The chat tool UIs map that to
 * a `ChatToolLimitCard` so those limits get the same branded, actionable card
 * as the token quota instead of leaving the user with model-only prose.
 */
export type ChatToolLimitKind = 'subscription-required' | 'usage-limit-reached';

// Titles for the per-tool limit cards, kept beside the token-limit copy so all
// in-chat limit wording lives in one place. The InfoCircle icon and the
// tier-aware CTA are shared with the token card (Janne, PLT-5273: one info icon
// for every limit card); only the title + detail differ.
const TOOL_LIMIT_TITLES: Record<ChatToolLimitKind, string> = {
  'usage-limit-reached': 'You’ve reached your usage limit',
  'subscription-required': 'Subscriber-only feature',
};

/**
 * Consultant self-serve upgrade CTA: opens the existing Starter→Pro upgrade
 * modal for the blocked client company. Only mounted when the caller is
 * eligible (resolved by `TokenLimitCta`), because the modal stack itself
 * requires a `CsrdCompanyProvider` and an admin who can pay.
 */
function ConsultantUpgradeCta({ companyId, companyName }: { companyId: string; companyName: string }) {
  const user = useGetLoggedInUser();
  const organization = user ? getOrganizationOfUser(user) : undefined;
  const upgradeFlow = useUpgradeFlow({ companyId, enabled: true });

  return (
    <>
      <button type="button" className="chat-token-limit-button" onClick={upgradeFlow.openTierSelection}>
        Upgrade to Pro
      </button>
      <TierSelectionModal
        isOpen={upgradeFlow.step === 'tier-selection'}
        onClose={upgradeFlow.closeTierSelection}
        onSelectPro={upgradeFlow.selectPro}
        companyName={companyName}
        organizationName={organization?.name ?? ''}
      />
      <UpgradeStripeCheckoutModal
        isOpen={upgradeFlow.step === 'stripe-checkout'}
        onClose={upgradeFlow.closeCheckout}
        fetchClientSecret={upgradeFlow.fetchUpgradeClientSecret}
      />
      <UpgradeConfirmingPaymentModal
        isOpen={upgradeFlow.step === 'confirming-payment'}
        timedOut={upgradeFlow.paymentConfirmationTimedOut}
        onClose={upgradeFlow.closeSuccess}
      />
      <UpgradeSuccessModal isOpen={upgradeFlow.step === 'success'} onClose={upgradeFlow.closeSuccess} />
      <UpgradeErrorModal
        isOpen={upgradeFlow.step === 'error'}
        onClose={upgradeFlow.closeError}
        errorMessage={upgradeFlow.checkoutError}
      />
    </>
  );
}

function TokenLimitCta({ companyId }: { companyId?: string }) {
  const variant = useTokenLimitCtaVariant();
  const navigateWithSearch = useNavigateWithSearch();
  const user = useGetLoggedInUser();
  const org = user ? getOrganizationOfUser(user) : undefined;
  const isConsultant = isDmaConsultantUser(user);

  // The consultant upgrade modal navigates/labels off the CSRD company context,
  // so it is only safe to offer when that provider is present (i.e. company-
  // scoped chat). Org-level chat has no provider → fall back to the contact CTA.
  const csrdContext = useMaybeCsrdCompanyContext();

  // Org role is needed for the Freemium starter gate and the consultant
  // org-admin check; skip the request for the other (contact-only) variants.
  const needsOrgRole = variant === 'starter' || isConsultant;
  const { role, isLoading: isRoleLoading } = useOrganizationAccess(needsOrgRole ? org?.id : undefined);
  const isOrgAdmin = role === 'ADMIN';

  // Consultant company-level admin (mirrors the dashboard / user-menu logic):
  // an org admin OR a company admin of the specific client company may upgrade.
  const companyUserGroupsQuery = useGetCompanyUserGroups(isConsultant ? companyId : undefined);
  const isCompanyAdmin = companyUserGroupsQuery.data?.currentUserCompanyRole === 'ADMIN';

  // For consultants the tier is per client company; only a Starter company can
  // be self-serve upgraded to Pro.
  const { data: companySubData, isLoading: isCompanySubLoading } = useGetCompanySubscription(
    companyId ?? '',
    isConsultant && !!companyId,
  );
  const isConsultantStarter = isConsultant && !isCompanySubLoading && companySubData?.subscription.tier === 'starter';

  const canConsultantUpgrade = !!companyId && !!csrdContext && isConsultantStarter && (isOrgAdmin || isCompanyAdmin);

  // Hold while the consultant eligibility signals are still resolving so the
  // CTA doesn't flash the generic "contact us" wording and then flip to the
  // upgrade button.
  if (
    isConsultant &&
    !!companyId &&
    !!csrdContext &&
    (isRoleLoading || companyUserGroupsQuery.isLoading || isCompanySubLoading)
  ) {
    return null;
  }

  if (canConsultantUpgrade) {
    return <ConsultantUpgradeCta companyId={companyId} companyName={csrdContext.companyName ?? ''} />;
  }

  if (variant === 'starter') {
    // Hold the CTA until the role resolves so it doesn't flip from the
    // non-admin hint to the upgrade button (or vice versa) on first paint.
    if (!org || isRoleLoading) {
      return null;
    }

    if (role === 'ADMIN') {
      return (
        <button type="button" className="chat-token-limit-button" onClick={() => navigateWithSearch(PLANS_PATH)}>
          Upgrade to Pro
        </button>
      );
    }

    // Non-admins can't self-upgrade and have no Plans & billing menu entry, so
    // point them at the person who can rather than a dead-end disabled button.
    return <span className="chat-token-limit-detail">Contact your organization admin to upgrade.</span>;
  }

  if (variant === 'pro') {
    return (
      <a className="chat-token-limit-link" href={`mailto:${SUPPORT_EMAIL}`}>
        Contact us to increase your limit
      </a>
    );
  }

  // 'enterprise' — investors and enterprise customers have a dedicated
  // account manager; the mailto is a safe fallback contact route.
  return (
    <a className="chat-token-limit-link" href={`mailto:${SUPPORT_EMAIL}`}>
      Contact your account manager
    </a>
  );
}

/**
 * Shared frame for every in-chat usage-limit card (PLT-5273): the lilac info
 * card with the InfoCircle icon, a title, a detail line, and the tier-aware
 * CTA. The token-quota card and the per-tool quota cards both render through
 * this so they read as one consistent affordance — same icon, same styling,
 * same upgrade/contact CTA — differing only in their title/detail copy.
 */
function LimitCard({ title, detail, companyId }: { title: string; detail: string; companyId?: string }) {
  return (
    <div className="chat-token-limit" role="note">
      <InfoCircle className="chat-token-limit-icon" width={18} height={18} aria-hidden="true" />
      <div className="chat-token-limit-body">
        <span className="chat-token-limit-title">{title}</span>
        <span className="chat-token-limit-detail">{detail}</span>
        <div className="chat-token-limit-cta">
          <TokenLimitCta companyId={companyId} />
        </div>
      </div>
    </div>
  );
}

export function ChatTokenLimitCard({ companyId }: { companyId?: string }) {
  return (
    <LimitCard
      title="You’ve reached your usage limit"
      detail="You’ve used all of your AI chat usage for now."
      companyId={companyId}
    />
  );
}

/**
 * PLT-5273: card shown when a per-tool quota (compute_financial_effects, the
 * climate-risk / site tools) is hit.
 *
 * `feature` is the human-readable name of the capped feature, supplied by the
 * calling tool UI (which knows its own tool). Naming it keeps this card clearly
 * distinct from the global AI-chat token limit, so the user understands only
 * that feature is exhausted — not their whole chat. Omitted → a feature-neutral
 * fallback. Not used for `subscription-required`, whose copy is already specific.
 *
 * `companyId` is optional — the limit tool result doesn't carry one, so the CTA
 * falls back to the tier-based contact / upgrade variant rather than the
 * consultant self-serve flow.
 */
export function ChatToolLimitCard({
  kind,
  feature,
  companyId,
}: {
  kind: ChatToolLimitKind;
  feature?: string;
  companyId?: string;
}) {
  const detail =
    kind === 'subscription-required'
      ? 'Company-wide assessment of this kind requires a subscription. You can still ask about individual sites or locations.'
      : feature
        ? `You’ve used all of your available ${feature} for now.`
        : 'You’ve used all of your available uses of this feature for now.';
  return <LimitCard title={TOOL_LIMIT_TITLES[kind]} detail={detail} companyId={companyId} />;
}
